import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { TriangleAlertIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

export interface ErrorContainerProps {
  className?: string;
  errorMessage: string;
  fontSize?: string;
  isLoading?: boolean;
  refetch?: () => unknown | Promise<unknown>;
}

export const ErrorContainer: FC<ErrorContainerProps> = ({
  className,
  errorMessage,
  fontSize,
  isLoading = false,
  refetch,
}) => {
  const { t } = useTranslation();

  return (
    <Card
      className={cn(
        'border-destructive/40 bg-destructive/5 text-destructive',
        className
      )}>
      <CardHeader className='gap-4'>
        <CardDescription
          className='flex items-center gap-2 text-destructive'
          style={fontSize ? { fontSize } : undefined}>
          <TriangleAlertIcon
            aria-hidden='true'
            className='size-4 shrink-0'
          />
          <span>{errorMessage}</span>
        </CardDescription>
        {refetch && (
          <Button
            type='button'
            disabled={isLoading}
            variant='outline'
            className='w-fit border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive'
            onClick={async () => {
              await refetch();
            }}>
            {isLoading && <Spinner />}
            {t('general.action.tryAgain')}
          </Button>
        )}
      </CardHeader>
    </Card>
  );
};

export const FullscreenErrorContainer: FC<ErrorContainerProps> = ({
  className,
  errorMessage,
  fontSize,
  isLoading,
  refetch,
}) => (
  <div
    className={cn(
      'flex h-full min-h-64 w-full items-center justify-center',
      className
    )}>
    <ErrorContainer
      className='w-full max-w-xl'
      errorMessage={errorMessage}
      fontSize={fontSize}
      isLoading={isLoading}
      refetch={refetch}
    />
  </div>
);
